import { Fragment, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { TERMS, type GlossaryTerm } from "@/content/glossary";

/** Extra phrases that should point at a term, beyond the term name itself. */
const EXTRA_ALIASES: Record<string, string[]> = {
  "cap-table": ["capitalisation table", "capitalization table"],
  "burn-rate": ["net burn", "gross burn"],
  "safe-note": ["SAFE", "SAFEs"],
  "equity-dilution": ["dilution", "diluted"],
  "vesting-schedule": ["vesting", "cliff"],
  "valuation-cap": ["cap"],
  "pre-money-vs-post-money-valuation": ["pre-money", "post-money"],
  "break-even-point": ["break-even", "breakeven"],
  "customer-acquisition-cost": ["CAC", "customer acquisition cost"],
  "lifetime-value": ["LTV", "lifetime value"],
  "mrr": ["MRR", "monthly recurring revenue"],
  "arr": ["ARR", "annual recurring revenue"],
  "mvp": ["MVP", "minimum viable product"],
  "tam-sam-som": ["TAM", "SAM", "SOM"],
  "ein": ["EIN"],
  "nda": ["NDA", "NDAs"],
  "product-market-fit": ["product/market fit"],
  "north-star-metric": ["north star"],
  "qualified-lead": ["qualified leads"],
  "retention-rate": ["retention"],
  "churn-rate": ["churn rate"],
  "gross-margin": ["gross margins"],
  "net-margin": ["net margins"],
};

/** Terms whose bare name is too ambiguous to auto-link. */
const NO_BARE_NAME = new Set([
  "llc-vs-s-corp-vs-c-corp",
  "organic-vs-paid-traffic",
  "customer-churn",
  "pre-money-vs-post-money-valuation",
  "tam-sam-som",
  "valuation-cap",
]);

interface Alias {
  phrase: string;
  slug: string;
}

const ALIASES: Alias[] = (() => {
  const out: Alias[] = [];
  for (const t of TERMS) {
    if (!NO_BARE_NAME.has(t.slug)) {
      const bare = t.term.replace(/\s*\(.*\)\s*/, "").trim();
      if (bare.length > 2) out.push({ phrase: bare, slug: t.slug });
    }
    for (const extra of EXTRA_ALIASES[t.slug] ?? []) {
      out.push({ phrase: extra, slug: t.slug });
    }
  }
  // Longest phrases first so "monthly recurring revenue" wins over "revenue".
  return out.sort((a, b) => b.phrase.length - a.phrase.length);
})();

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const PATTERN = new RegExp(
  `\\b(${ALIASES.map((a) => escapeRegExp(a.phrase)).join("|")})(s\\b|\\b)`,
  "gi",
);

function slugForMatch(match: string): string | undefined {
  const needle = match.toLowerCase().replace(/s$/, "");
  const hit =
    ALIASES.find((a) => a.phrase.toLowerCase() === match.toLowerCase()) ??
    ALIASES.find((a) => a.phrase.toLowerCase() === needle);
  return hit?.slug;
}

/**
 * Turns the first mention of each other glossary term into a link.
 * `used` is shared across paragraphs so a term links once per page.
 */
export function linkifyParagraph(
  text: string,
  currentSlug: string,
  used: Set<string>,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  PATTERN.lastIndex = 0;

  for (const match of text.matchAll(PATTERN)) {
    const raw = match[0];
    const index = match.index ?? 0;
    const slug = slugForMatch(raw);
    if (!slug || slug === currentSlug || used.has(slug)) continue;

    used.add(slug);
    if (index > last) nodes.push(<Fragment key={`t${key++}`}>{text.slice(last, index)}</Fragment>);
    nodes.push(
      <Link
        key={`l${key++}`}
        to="/glossary/$slug"
        params={{ slug }}
        className="text-foreground underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground/60"
      >
        {raw}
      </Link>,
    );
    last = index + raw.length;
  }

  if (last < text.length) nodes.push(<Fragment key={`t${key++}`}>{text.slice(last)}</Fragment>);
  return nodes;
}

/** Terms mentioned in this entry's body, used for a "Related terms" row. */
export function relatedTerms(term: GlossaryTerm, limit = 6): GlossaryTerm[] {
  const text = [term.definition, ...term.body].join(" ");
  const slugs = new Set<string>();
  PATTERN.lastIndex = 0;
  for (const match of text.matchAll(PATTERN)) {
    const slug = slugForMatch(match[0]);
    if (slug && slug !== term.slug) slugs.add(slug);
  }
  return TERMS.filter((t) => slugs.has(t.slug)).slice(0, limit);
}
